import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import arcLogo from "@/assets/arc-logo.png";
import { ThemeToggle } from "@/components/Layout";
import { motion } from "framer-motion";
import { Gift, Gavel, Star, Image, Users, ShieldCheck, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";

const HERO = "https://images.unsplash.com/photo-1554907984-15263bfd63bd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const features = [
  { icon: Image, title: "Virtual Gallery", desc: "Collect from ~1,000 curated public-domain masterpieces." },
  { icon: Star, title: "Daily Reviews", desc: "Review your art in 30 seconds and earn instant rewards." },
  { icon: Gavel, title: "Weekly Auctions", desc: "Weekly auctions for the club's finest pieces." },
  { icon: Users, title: "Refer & Earn", desc: "Invite friends and grow your Reward Wallet together." },
];

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (user) nav("/dashboard"); }, [user, nav]);

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <header className="glass-nav bg-background/70 border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={arcLogo} alt="Art Review Club" className="h-9 w-9 rounded-xl object-contain shrink-0" />
            <span className="font-heading text-sm sm:text-xl font-bold truncate">Art Review Club</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <ThemeToggle />
            <Button variant="ghost" size="sm" className="px-2 sm:px-3" onClick={() => nav("/login")} data-testid="header-login-btn">Log in</Button>
            <Button size="sm" className="rounded-full navy-gradient text-white font-semibold px-3 sm:px-4" onClick={() => nav("/register")} data-testid="header-join-btn">Join Now</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10 items-center py-16 md:py-24">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-gold bg-gold/10 px-3 py-1.5 rounded-full">
              <Gift className="h-3.5 w-3.5" /> <span className="navy-text">₦500 Welcome Cash-back</span>
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none mt-5">
              Collect art. Review daily. <span className="navy-text">Win auctions.</span>
            </h1>
            <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-lg">
              A members-only virtual art collecting club. Join today and receive an
              instant <b className="navy-text">Welcome Cash-back</b> credited to your Reward Wallet.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Button size="lg" className="rounded-full h-12 px-8 navy-gradient text-white font-semibold text-base" onClick={() => nav("/register")} data-testid="hero-join-btn">
                Join Now <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-8" onClick={() => nav("/login")} data-testid="hero-login-btn">
                I'm a member
              </Button>
            </div>
            <p className="flex items-center gap-2 text-xs text-muted-foreground mt-5">
              <ShieldCheck className="h-4 w-4 text-gold" /> One-time fee • Lifetime access • Paystack secured
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="absolute -inset-6 navy-gradient opacity-20 blur-3xl rounded-full" />
            <img src={HERO} alt="Luxury gallery" className="relative rounded-3xl border border-border shadow-2xl w-full object-cover aspect-[4/5]" />
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="bg-card rounded-2xl p-6 border border-border">
              <div className="h-11 w-11 rounded-xl bg-primary grid place-items-center"><f.icon className="h-5 w-5 text-primary-foreground" /></div>
              <h3 className="font-heading font-semibold text-lg mt-4">{f.title}</h3>
              <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-24">
        <div className="rounded-3xl bg-primary text-primary-foreground p-10 md:p-16 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 h-64 w-64 navy-gradient opacity-30 blur-3xl rounded-full" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold relative">Your ₦500 Welcome Cash-back is waiting.</h2>
          <p className="text-primary-foreground/80 mt-3 relative">Join the club, collect your first artwork and start earning today.</p>
          <Button size="lg" className="rounded-full h-12 px-8 mt-8 bg-white text-primary hover:bg-white/90 font-semibold relative" onClick={() => nav("/register")} data-testid="cta-join-btn">
            Get Started
          </Button>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-8">Artworks are virtual copyright-free collectible assets. © Art Review Club.</p>
        <p className="text-center text-[11px] text-muted-foreground/70 mt-2">Art Review Club is purely a recreational club and it is voluntary to join.</p>
      </section>
    </div>
  );
}
